import { useEffect, useRef } from 'react';
import { useFreemiumUrlState } from './use-freemium-url-state';
import { useFreemiumSelection } from './use-freemium-selection';
import { useFreemiumCheckoutStore } from '../stores/freemium-checkout-store';

import { analytics } from '@/analytics';

function safeTrack(eventName: string, properties: Record<string, unknown>) {
  try {
    analytics.track(eventName, properties, 'action');
  } catch {
    // Metrics must never break the paywall flow.
  }
}

export function useFreemiumGateFlow(clientId: string) {
  const { step, intent, selectedTier, setIntent, goToPaywall, goToIntent } = useFreemiumUrlState();
  const clientSecret = useFreemiumCheckoutStore(state => state.clientSecret);
  const selection = useFreemiumSelection(clientId);

  const hitTracked = useRef(false);

  useEffect(() => {
    if (!hitTracked.current) {
      hitTracked.current = true;
      safeTrack('freemium_paywall_hit', { clientId });
    }
  }, [clientId]);

  const recreateRequested = useRef(false);

  useEffect(() => {
    if (
      step === 'checkout' &&
      selectedTier &&
      !clientSecret &&
      !selection.isPending &&
      !recreateRequested.current
    ) {
      recreateRequested.current = true;
      selection.recreateCheckout(selectedTier);
    }
  }, [step, selectedTier, clientSecret, selection]);

  function continueToPaywall() {
    goToPaywall();
    if (intent) {
      safeTrack('freemium_intent_selected', { clientId, intent });
    }
  }

  return {
    step,
    intent,
    selectedTier,
    clientSecret,
    setIntent,
    continueToPaywall,
    goToPaywall,
    goToIntent,
    ...selection,
  };
}
